<template>
    <div class="enquiry-form">
        <AuthFormInModal
            ref="authForm"
            :content
            :is-open="!!enquiryType && !hideForm"
            :registration-disabled="fetching"
            :registration-errors="errors"
            @close="closeEnquiryForm"
            @login="(_, type) => login(_, type)"
            @register="register"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useEnquiryFormStore } from '@/stores/enquiryForm'

import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'
import { useCountryPlugin } from '@/plugins/country.ts'

import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useCreateEnquiryNewCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryNewCustomer.generated.ts'
import { useCreateEnquiryExistingCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryExistingCustomer.generated.ts'

import AuthFormInModal from '../user/auth/AuthFormInModal.vue'
import { useCurrentUrl } from '@/plugins/current-url.ts'
import { useInit } from '@/plugins/init/use-init.ts'
import { assertNever } from '@/helpers'
import type { EnquiryType, LoginResponseSuccessFragment } from '@/graphql/api'
import { ROUTE_PROFILE_DESIGN_STUDIO } from '@/router'
import type { LoginType } from '@/components/user/auth/AuthForm.vue'

const enquiryFormStore = useEnquiryFormStore()
const { closeEnquiryForm } = enquiryFormStore
const { enquiryType, enquiryImage, isFetching } = storeToRefs(enquiryFormStore)

const { enquiryFormContent } = useInit()

const content = computed(() => {
    if (!enquiryType.value) {
        return undefined
    }

    switch (enquiryType.value) {
        case 'BLOG_POST':
            return enquiryFormContent.value['enquiry-type-blog-post']

        case 'ENGAGEMENT_RING':
            return enquiryFormContent.value['enquiry-type-engagement-ring']

        case 'ENGAGEMENT_RING_BROWSE':
            return enquiryFormContent.value[
                'enquiry-type-engagement-ring-browse'
            ]

        case 'ENGAGEMENT_RING_IMAGE_ATTACHED_LANDING':
            return enquiryFormContent.value[
                'enquiry-type-engagement-ring-image-attached-landing'
            ]

        case 'JEWELLERY_PRODUCT_LANDING_GENERIC':
            return enquiryFormContent.value[
                'enquiry-type-jewellery-product-landing-generic'
            ]

        case 'LOCATION_SIGNUP':
            return enquiryFormContent.value['enquiry-type-location-signup']

        case 'WEDDING_RING':
            return enquiryFormContent.value['enquiry-type-wedding-ring']

        case 'WEDDING_RING_BESPOKE_LANDING':
            return enquiryFormContent.value[
                'enquiry-type-wedding-ring-bespoke-landing'
            ]
        default:
            return assertNever(enquiryType.value, undefined)
    }
})

const commissionJunction = getCommissionJunctionInput()

const currentUrl = useCurrentUrl()
const currency = useCurrencyInput()
const { countryCode } = useCountryPlugin()
const { isLogged } = useAuth()

const newCustomerResponse = useCreateEnquiryNewCustomerMutation()
const { executeMutation: newCustomerMutation, fetching: newCustomerFetching } =
    newCustomerResponse

const existingCustomerResponse = useCreateEnquiryExistingCustomerMutation()
const {
    executeMutation: existingCustomerMutation,
    fetching: existingCustomerFetching,
} = existingCustomerResponse

const fetching = computed(
    () => newCustomerFetching.value || existingCustomerFetching.value
)

watch(fetching, (n) => (isFetching.value = n))

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createEnquiryExistingCustomer'
    ),
    createResponseAndPath(newCustomerResponse, 'createEnquiryNewCustomer'),
])

const enquiryInput = computed(() => {
    if (!enquiryType.value) {
        return null
    }

    const uri = currentUrl.value.href

    return {
        baseInput: {
            uri,
            currency: currency.value,
            country: countryCode.value!,
        },
        image: enquiryImage.value,
        uri,
        type: enquiryType.value,
        commissionJunction,
    }
})

const hideForm = ref(false)

watch(
    enquiryType,
    (type) => {
        const input = enquiryInput.value

        if (!input) {
            return
        }

        if (!type) {
            return
        }

        if (!isLogged.value) {
            return
        }

        hideForm.value = true

        existingCustomerMutation({ input }).then(({ data, error }) => {
            if (error) {
                hideForm.value = false
                // Error state should be displayed at this point
                return
            }

            if (data && data.createEnquiryExistingCustomer) {
                authForm.value?.handleRegistrationResponse?.(
                    undefined,
                    getRedirect(type)
                )

                closeEnquiryForm()
                hideForm.value = false
            }
        })
    },
    { flush: 'post' }
)

const { customerRegistrationInput, googleSignInToken, facebookSignInToken } =
    storeToRefs(useCustomerRegistrationInputStore())

const isAdmin = useIsAdmin()

const submitExistingCustomer = () => {
    const input = enquiryInput.value

    if (!input) {
        return
    }

    return existingCustomerMutation({ input }).then(({ data, error }) => {
        if (error) {
            return undefined
        }

        return data?.createEnquiryExistingCustomer
    })
}

const submitNewCustomer = () => {
    const input = enquiryInput.value

    if (!input) {
        return
    }

    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    return newCustomerMutation({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then(({ data, error }) => {
        if (error) {
            googleSignInToken.value = null
            facebookSignInToken.value = null

            return undefined
        }

        return data?.createEnquiryNewCustomer
    })
}

const getRedirect = (
    enquiryType: EnquiryType | null
): string | { name: string } => {
    switch (enquiryType) {
        case 'ENGAGEMENT_RING':
        case 'ENGAGEMENT_RING_IMAGE_ATTACHED_LANDING':
            switch (countryCode.value) {
                case 'GB':
                    return '/type-form/view/dHNtS0jG'
                case 'US':
                    return '/us/type-form/view/R1ZlBnod'
                default:
                    return { name: ROUTE_PROFILE_DESIGN_STUDIO }
            }
        case 'LOCATION_SIGNUP':
            return '/about-us/contacts'
        default:
            return { name: ROUTE_PROFILE_DESIGN_STUDIO }
    }
}

const authForm = useTemplateRef('authForm')

const login = async (_: LoginResponseSuccessFragment, type: LoginType) => {
    switch (type) {
        case 'manual':
            {
                const data = await submitExistingCustomer()

                if (!data) {
                    // Error state should be displayed at this point
                    return
                }

                const redirect = getRedirect(enquiryType.value)
                authForm.value?.handleRegistrationResponse?.(
                    undefined,
                    redirect
                )
            }
            break
        case 'auto':
            {
                const redirect = getRedirect(enquiryType.value)
                authForm.value?.handleRegistrationResponse?.(
                    undefined,
                    redirect
                )
            }
            break
        default:
            assertNever(type, undefined)
    }
}

const register = async (successCallback: () => void) => {
    const data = await submitNewCustomer()

    if (!data) {
        // Error state should be displayed at this point
        return
    }

    const redirect = getRedirect(enquiryType.value)

    authForm.value?.handleRegistrationResponse?.(
        data.__typename === 'EnquiryResponseNewCustomer'
            ? data.customer
            : undefined,
        redirect
    )

    successCallback()
}
</script>
